"use client"

import { motion } from "motion/react"
import { ArrowRight, Building2, Cpu, Cuboid, Terminal } from "lucide-react"
import Image from "next/image"
import Link from "next/link"

import SpotlightCard from "@/core/ui/effects/SpotlightCard"
import { getServiceTheme } from "@/core/utils/service-theme"

interface SubServiceCardProps {
    id: string
    categoryId: string
    title: string
    description: string
    image?: string
    index: number
}

export default function SubServiceCard({ id, categoryId, title, description, image, index }: SubServiceCardProps) {
    const theme = getServiceTheme(categoryId)

    // Variant Renderers
    const renderBlockchainCard = () => (
        <SpotlightCard
            className={`h-full flex flex-col p-6 border-2 border-dashed ${theme.accentBorder} bg-card/60 backdrop-blur-md hover:bg-card/80 transition-all duration-500 group relative`}
            spotlightColor={`${theme.hex}1A`} // 10% opacity
        >
            {/* Decorative corner blocks */}
            <div className={`absolute top-0 left-0 w-2 h-2 bg-${theme.accentColor.split('-')[1]}-500/50`} />
            <div className={`absolute top-0 right-0 w-2 h-2 bg-${theme.accentColor.split('-')[1]}-500/50`} />
            <div className={`absolute bottom-0 left-0 w-2 h-2 bg-${theme.accentColor.split('-')[1]}-500/50`} />
            <div className={`absolute bottom-0 right-0 w-2 h-2 bg-${theme.accentColor.split('-')[1]}-500/50`} />

            <div className="flex items-center gap-3 mb-6 border-b border-border/10 pb-4">
                <div className={`p-2 rounded-none border ${theme.accentBorder} bg-black dark:bg-black bg-slate-900 text-${theme.accentColor.split('-')[1]}-400`}>
                    <Cuboid className="w-5 h-5" />
                </div>
                <div className="font-mono text-xs text-muted-foreground tracking-wider">
                    BLOCK_{index < 9 ? `0${index + 1}` : index + 1}
                </div>
                <div className={`ml-auto text-[10px] font-mono ${theme.accentColor}`}>
                    {/* eslint-disable-next-line react-hooks/purity */}
                    0x{Math.random().toString(16).substr(2, 4).toUpperCase()}...
                </div>
            </div>

            <h3 className={`text-xl font-bold text-foreground mb-3 font-mono group-hover:${theme.accentColor} transition-colors`}>
                {title}
            </h3>

            <p className="text-muted-foreground text-sm leading-relaxed mb-6 font-mono border-l-2 border-border/20 pl-4 grow">
                {description}
            </p>

            <div className="mt-auto flex justify-between items-center pt-4 border-t border-dashed border-border/20">
                <span className={`text-xs font-mono text-${theme.accentColor.split('-')[1]}-400/70`}>
                    [ INITIALIZE ]
                </span>
                <ArrowRight className={`w-4 h-4 text-muted-foreground/50 group-hover:${theme.accentColor} transition-colors`} />
            </div>
        </SpotlightCard>
    )

    const renderGenAICard = () => (
        <SpotlightCard
            className={`h-full flex flex-col p-8 rounded-4xl border ${theme.accentBorder} bg-linear-to-br from-card/30 to-transparent backdrop-blur-md hover:from-card/50 transition-all duration-500 group overflow-hidden`}
            spotlightColor={`${theme.hex}26`}
        >
            {/* Soft Glow Background */}
            <div className={`absolute top-0 right-0 w-32 h-32 ${theme.accentBg} blur-[60px] rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-700`} />

            <div className="relative z-10 mb-6 flex justify-between items-start">
                <div className={`w-12 h-12 rounded-2xl bg-muted/50 flex items-center justify-center text-${theme.accentColor.split('-')[1]}-400 group-hover:scale-110 transition-transform duration-500`}>
                    <Cpu className="w-6 h-6" />
                </div>
                <span className={`px-3 py-1 rounded-full text-[10px] font-medium border ${theme.accentBorder} ${theme.accentBg} ${theme.accentColor}`}>
                    NEURAL_MOD
                </span>
            </div>

            <h3 className={`relative z-10 text-2xl font-bold text-foreground mb-3 group-hover:${theme.accentColor} transition-colors`}>
                {title}
            </h3>

            <p className="relative z-10 text-muted-foreground text-sm leading-relaxed mb-8 grow">
                {description}
            </p>

            <div className="relative z-10 mt-auto flex items-center gap-2 text-xs font-medium text-muted-foreground/50 group-hover:text-foreground transition-colors">
                <div className={`w-2 h-2 rounded-full ${theme.accentBg.replace('bg-', 'bg-')} animate-pulse`} style={{ backgroundColor: theme.hex }} />
                Training Complete
                <ArrowRight className="w-4 h-4 ml-auto transform group-hover:translate-x-1 transition-transform" />
            </div>
        </SpotlightCard>
    )

    const renderEnterpriseCard = () => (
        <SpotlightCard
            className={`h-full flex flex-col border-l-4 ${theme.accentBorder.replace('border-', 'border-l-')} border-y border-r border-border/20 bg-card/60 backdrop-blur-md hover:bg-card/80 transition-colors duration-500 group`}
            spotlightColor={`${theme.hex}1A`}
        >
            <div className="relative h-48 w-full overflow-hidden border-b border-border/20">
                <Image
                    src={image || "/placeholder.svg"}
                    alt={title}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-105 opacity-80 group-hover:opacity-100 grayscale group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-grid-white/[0.05] dark:bg-grid-white/[0.05] bg-grid-black/[0.05]" />
                <div className="absolute bottom-0 left-0 w-full h-1/2 bg-linear-to-t from-black/80 to-transparent" />

                <div className="absolute top-4 left-4 flex gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full" />
                    <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                </div>
            </div>

            <div className="p-8 flex flex-col grow">
                <div className="flex items-center justify-between mb-4">
                    <Building2 className={`w-5 h-5 text-${theme.accentColor.split('-')[1]}-500`} />
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Enterprise Scale</span>
                </div>

                <h3 className={`text-xl font-bold text-foreground mb-3 group-hover:${theme.accentColor} transition-colors`}>
                    {title}
                </h3>

                <p className="text-muted-foreground text-sm leading-relaxed mb-6 grow">
                    {description}
                </p>

                <div className={`mt-auto pt-4 border-t border-border/20 flex justify-between items-center text-xs font-medium text-${theme.accentColor.split('-')[1]}-400`}>
                    <span>View Solution</span>
                    <ArrowRight className="w-4 h-4" />
                </div>
            </div>
        </SpotlightCard>
    )

    const renderDevelopmentCard = () => (
        <SpotlightCard
            className={`h-full flex flex-col bg-card border border-border/20 hover:border-${theme.accentColor.split('-')[1]}-500/50 transition-colors duration-500 group rounded-lg overflow-hidden`}
            spotlightColor={`${theme.hex}15`}
        >
            {/* Terminal Header */}
            <div className="px-4 py-2 bg-muted/30 border-b border-border/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Terminal className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] font-mono text-muted-foreground">module.tsx</span>
                </div>
                <div className={`w-2 h-2 rounded-full bg-${theme.accentColor.split('-')[1]}-500/50`} />
            </div>

            <div className="p-6 flex flex-col grow font-mono">
                <div className="text-muted-foreground/70 text-xs mb-4">
                    {"// "}{title.toLowerCase().replace(/\s+/g, '-')}
                </div>

                <h3 className={`text-lg font-bold text-foreground mb-2 group-hover:${theme.accentColor} transition-colors`}>
                    <span className="text-pink-500">const</span> {title} <span className="text-pink-500">=</span> <span className="text-yellow-500 dark:text-yellow-300">()</span> <span className="text-pink-500">={">"}</span>
                </h3>

                <div className="pl-4 border-l border-border/20 mb-6 grow">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                        <span className="text-gray-500 dark:text-gray-600">{"/* "}</span>
                        {description}
                        <span className="text-gray-500 dark:text-gray-600">{" */"}</span>
                    </p>
                </div>

                <div className="mt-auto flex items-center gap-2 text-xs">
                    <span className="text-yellow-500 dark:text-yellow-300">return</span>
                    <span className={`text-${theme.accentColor.split('-')[1]}-400`}>&lt;Deploy /&gt;</span>
                    <span className="w-2 h-4 bg-foreground/50 animate-pulse ml-1 block" />
                </div>
            </div>
        </SpotlightCard>
    )

    const renderContent = () => {
        switch (categoryId) {
            case 'blockchain': return renderBlockchainCard()
            case 'genai': return renderGenAICard()
            case 'enterprise': return renderEnterpriseCard()
            case 'development': return renderDevelopmentCard()
            default: return renderDevelopmentCard()
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="h-full"
        >
            <Link href={`/services/${categoryId}/${id}`} className="block h-full cursor-none-substitute">
                {renderContent()}
            </Link>
        </motion.div>
    )
}
